'use client';

import { c as _c } from "react/compiler-runtime";
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import * as React from 'react';
import { type DayButton, DayPicker, type DropdownProps, getDefaultClassNames } from 'react-day-picker';
import { cn } from '../../utils/cn';
import { Button, buttonVariants } from './Button';
import { Select, SelectContent, SelectItem, SelectTrigger } from './Select';
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'label',
  buttonVariant = 'ghost',
  formatters,
  components,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>['variant'];
}) {
  const defaultClassNames = getDefaultClassNames();
  return <DayPicker showOutsideDays={showOutsideDays} className={cn('bg-background group/calendar p-3 [--cell-size:--spacing(8)] [[data-slot=card-content]_&]:bg-transparent [[data-slot=popover-content]_&]:bg-transparent', String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`, String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`, className)} captionLayout={captionLayout} formatters={{
    formatMonthDropdown: date => date.toLocaleString('default', {
      month: 'short'
    }),
    ...formatters
  }} classNames={{
    root: cn('w-fit', defaultClassNames.root),
    months: cn('flex gap-4 flex-col md:flex-row relative', defaultClassNames.months),
    month: cn('flex flex-col w-full gap-4', defaultClassNames.month),
    nav: cn('flex items-center gap-1 w-full absolute top-0 inset-x-0 justify-between pointer-events-none', defaultClassNames.nav),
    button_previous: cn(buttonVariants({
      variant: buttonVariant
    }), 'size-(--cell-size) aria-disabled:opacity-50 p-0 select-none pointer-events-auto', defaultClassNames.button_previous),
    button_next: cn(buttonVariants({
      variant: buttonVariant
    }), 'size-(--cell-size) aria-disabled:opacity-50 p-0 select-none pointer-events-auto', defaultClassNames.button_next),
    month_caption: cn('flex items-center justify-center h-(--cell-size) w-full px-(--cell-size)', defaultClassNames.month_caption),
    dropdowns: cn('w-full flex items-center text-sm font-medium justify-center h-(--cell-size) gap-1.5', defaultClassNames.dropdowns),
    caption_label: cn('select-none font-medium text-sm', defaultClassNames.caption_label),
    table: 'w-full border-collapse',
    weekdays: cn('flex', defaultClassNames.weekdays),
    weekday: cn('text-muted-foreground rounded-md flex-1 font-normal text-[0.8rem] select-none', defaultClassNames.weekday),
    week: cn('flex w-full mt-2', defaultClassNames.week),
    week_number_header: cn('select-none w-(--cell-size)', defaultClassNames.week_number_header),
    week_number: cn('text-[0.8rem] select-none text-muted-foreground', defaultClassNames.week_number),
    day: cn('relative w-full h-full p-0 text-xs text-center [&:first-child[data-selected=true]_button]:rounded-l-md [&:last-child[data-selected=true]_button]:rounded-r-md group/day aspect-square select-none', defaultClassNames.day),
    range_start: cn('rounded-l-md bg-accent', defaultClassNames.range_start),
    range_middle: cn('rounded-none', defaultClassNames.range_middle),
    range_end: cn('rounded-r-md bg-accent', defaultClassNames.range_end),
    today: cn('bg-accent text-accent-foreground rounded-md data-[selected=true]:rounded-none', defaultClassNames.today),
    outside: cn('text-muted-foreground aria-selected:text-muted-foreground', defaultClassNames.outside),
    disabled: cn('text-muted-foreground opacity-50', defaultClassNames.disabled),
    hidden: cn('invisible', defaultClassNames.hidden),
    ...classNames
  }} components={{
    Root: ({
      className: className_0,
      rootRef,
      ...props_0
    }) => {
      return <div data-slot="calendar" ref={rootRef} className={cn(className_0)} {...props_0} />;
    },
    Chevron: ({
      className: className_1,
      orientation,
      ...props_1
    }) => {
      if (orientation === 'left') {
        return <ChevronLeftIcon className={cn('size-4', className_1)} {...props_1} />;
      }
      if (orientation === 'right') {
        return <ChevronRightIcon className={cn('size-4', className_1)} {...props_1} />;
      }
      return <ChevronDownIcon className={cn('size-4', className_1)} {...props_1} />;
    },
    DayButton: CalendarDayButton,
    Dropdown: CalendarDropdown,
    MonthsDropdown: props_2 => <CalendarDropdown {...props_2} testId="calendar-month-dropdown" />,
    YearsDropdown: props_3 => <CalendarDropdown {...props_3} testId="calendar-year-dropdown" />,
    WeekNumber: ({
      children,
      ...props_4
    }) => {
      return <td {...props_4}>
              <div className="flex size-(--cell-size) items-center justify-center text-center">
                {children}
              </div>
            </td>;
    },
    ...components
  }} {...props} />;
}
function CalendarDayButton(t0) {
  const $ = _c(19);
  let className;
  let day;
  let modifiers;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      day,
      modifiers,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = day;
    $[3] = modifiers;
    $[4] = props;
  } else {
    className = $[1];
    day = $[2];
    modifiers = $[3];
    props = $[4];
  }
  const defaultClassNames = getDefaultClassNames();
  const ref = React.useRef(null);
  let t1;
  let t2;
  if ($[5] !== modifiers.focused) {
    t1 = () => {
      if (modifiers.focused) {
        ref.current?.focus();
      }
    };
    t2 = [modifiers.focused];
    $[5] = modifiers.focused;
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  React.useEffect(t1, t2);
  const T0 = Button;
  const t3 = "ghost";
  const t4 = "icon";
  let t5;
  if ($[8] !== day.date) {
    t5 = day.date.toLocaleDateString();
    $[8] = day.date;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = modifiers.selected && !modifiers.range_start && !modifiers.range_end && !modifiers.range_middle;
  const t7 = modifiers.range_start;
  const t8 = modifiers.range_end;
  const t9 = modifiers.range_middle;
  const t10 = cn("data-[selected-single=true]:bg-primary data-[selected-single=true]:text-primary-foreground data-[range-middle=true]:bg-accent data-[range-middle=true]:text-accent-foreground data-[range-start=true]:bg-primary data-[range-start=true]:text-primary-foreground data-[range-end=true]:bg-primary data-[range-end=true]:text-primary-foreground group-data-[focused=true]/day:border-ring group-data-[focused=true]/day:ring-ring/50 dark:hover:text-accent-foreground flex aspect-square size-auto w-full min-w-(--cell-size) flex-col gap-1 leading-none font-normal group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:ring-[3px] data-[range-end=true]:rounded-md data-[range-end=true]:rounded-r-md data-[range-middle=true]:rounded-none data-[range-start=true]:rounded-md data-[range-start=true]:rounded-l-md [&>span]:text-xs [&>span]:opacity-70", defaultClassNames.day, className);
  let t11;
  if ($[10] !== T0 || $[11] !== modifiers.range_end || $[12] !== modifiers.range_middle || $[13] !== modifiers.range_start || $[14] !== props || $[15] !== t10 || $[16] !== t5 || $[17] !== t6) {
    t11 = <T0 ref={ref} variant={t3} size={t4} data-day={t5} data-selected-single={t6} data-range-start={t7} data-range-end={t8} data-range-middle={t9} className={t10} {...props} />;
    $[10] = T0;
    $[11] = modifiers.range_end;
    $[12] = modifiers.range_middle;
    $[13] = modifiers.range_start;
    $[14] = props;
    $[15] = t10;
    $[16] = t5;
    $[17] = t6;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  return t11;
}

/**
 * Month/year navigation rendered with the app's `Select` instead of react-day-picker's native
 * `<select>`, so it opens the same listbox as every other dropdown rather than the OS control.
 */
function CalendarDropdown(t0) {
  const $ = _c(20);
  const {
    options,
    value,
    onChange,
    disabled,
    className,
    "aria-label": ariaLabel,
    testId
  } = t0;
  let t1;
  if ($[0] !== options || $[1] !== value) {
    t1 = options?.find(option => option.value === value);
    $[0] = options;
    $[1] = value;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const selected = t1;
  const t2 = value === undefined ? undefined : String(value);
  let t3;
  if ($[3] !== onChange) {
    t3 = next => onChange?.({
      target: {
        value: next
      }
    } as React.ChangeEvent<HTMLSelectElement>);
    $[3] = onChange;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = selected?.label;
  let t5;
  if ($[5] !== ariaLabel || $[6] !== className || $[7] !== t4 || $[8] !== testId) {
    t5 = <SelectTrigger variant="outline" size="xs" aria-label={ariaLabel} className={className} data-testid={testId}>{t4}</SelectTrigger>;
    $[5] = ariaLabel;
    $[6] = className;
    $[7] = t4;
    $[8] = testId;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== options) {
    t6 = options?.map(_temp);
    $[10] = options;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t6) {
    t7 = <SelectContent>{t6}</SelectContent>;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== disabled || $[15] !== t2 || $[16] !== t3 || $[17] !== t5 || $[18] !== t7) {
    t8 = <Select value={t2} disabled={disabled} onValueChange={t3}>{t5}{t7}</Select>;
    $[14] = disabled;
    $[15] = t2;
    $[16] = t3;
    $[17] = t5;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
}
function _temp(option_0) {
  return <SelectItem key={option_0.value} value={String(option_0.value)} disabled={option_0.disabled}>{option_0.label}</SelectItem>;
}
export { Calendar, CalendarDayButton };